import Component from '@glimmer/component'
import { service } from '@ember/service'
import convertDateFormat from 'district-ui-client/utils/giraffe/convert-date-format'
import { formatNumber } from 'district-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import type { IntlService } from 'ember-intl'
import { LeftSplitChart } from 'district-ui-client/components/left-split-chart'
import { SummaryBox, SummaryBoxColorScheme } from 'district-ui-client/components/summary-box'
import { SeriesColumn } from 'district-ui-client/components/primitives/series-column/component'
import type { ReportingProductSlug } from 'district-ui-client/domain/product'
import { eq } from 'ember-truth-helpers'
import type { SeriesColumnOptions } from 'highcharts'
import type { AwardsEarnedTotalsByGradeData } from 'district-ui-client/utils/student-events/fetchers/awards-earned-totals'
import type { Stat } from 'district-ui-client/components/figure-boxes'

const computeGoldenEggs = (gold: number, silver: number, bronze: number) => {
  const totalSilver = gold * 3 + silver
  const totalBronze = totalSilver * 5 + bronze
  return totalBronze * 1000
}

/**
 * Puts together all the pieces for the awards earned totals chart.
 *
 * Data format:
 *
 * ```
 * {
 *   grade_position: 1,
 *   summary: [
 *     { category: 'gold', count: 90 },
 *     { category: 'silver', count: 123 },
 *     { category: 'bronze', count: 250 },
 *   ],
 *   months: [
 *     {
 *       gold: 20,
 *       silver: 23,
 *       bronze: 125,
 *       month: '2016-07',
 *     },
 *     {
 *       gold: 70,
 *       silver: 100,
 *       bronze: 125,
 *       month: '2016-08',
 *     },
 *   ],
 * ```
 */

interface Signature {
  Args: {
    data: AwardsEarnedTotalsByGradeData
    product: ReportingProductSlug
    grade?: string
    title?: string
    yAxisTitle?: string
  }
  Element: HTMLDivElement
}

export class ChartsAwardsEarnedTrophiesTotals extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Computed list of summary statistics, extracted from the input data.
   * Display total gold, silver and bronze counts.
   */
  get summaryStats(): Stat[] {
    const { intl } = this
    const { gold, silver, bronze } = this.args.data.summary

    const goldLabel = intl.t(`reporting.charts.awardsEarned.trophies.goldTrophy`)
    const silverLabel = intl.t(`reporting.charts.awardsEarned.trophies.silverMedallion`)
    const bronzeLabel = intl.t(`reporting.charts.awardsEarned.trophies.bronzeMedal`)
    const totalLabel = intl.t(`reporting.charts.awardsEarned.trophies.totalLabel`)

    const total = gold + silver + bronze

    return [
      { label: goldLabel, value: formatNumber(gold, 0) },
      { label: silverLabel, value: formatNumber(silver, 0) },
      { label: bronzeLabel, value: formatNumber(bronze, 0) },
      { label: totalLabel, value: formatNumber(total, 0) },
    ]
  }

  /**
   * Computed list of column data, extracted from the input data.
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    const goldenEggs = this.args.data?.months.map(({ gold, silver, bronze }) => computeGoldenEggs(gold, silver, bronze))

    return [{ name: 'Golden Eggs', data: goldenEggs, color: colors.juicyOrange300, type: 'column' }]
  }

  /**
   * Computed list of category data, extracted from the input data.
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories() {
    return this.args.data?.months?.map((m) => convertDateFormat(m.month)) ?? []
  }

  <template>
    <div ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @grade={{@grade}}
            @stats={{this.summaryStats}}
            @colorScheme={{if (eq @product "maths") SummaryBoxColorScheme.DarkGreen SummaryBoxColorScheme.LightBlue}}
          />
        </:left>
        <:right>
          <SeriesColumn
            @title={{@title}}
            @data={{this.columns}}
            @categories={{this.categories}}
            @stackingStyle="normal"
            @yAxisLabel={{@yAxisTitle}}
            @yAxisReversedStacks={{false}}
            @chartSpacing={{Array 10 10 10 10}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default ChartsAwardsEarnedTrophiesTotals
